import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MaskPhone } from '@config/mask.config';
import { RegexEmail, RegexOnlyLetters, RegexPhone } from '@config/regex.config';
import { ModalNewClientService } from './modal-new-client.service';
import { BrokerAccount, NewClient, ServerResponse, QuestionI } from '@models';
import { BookVisitService } from 'src/app/pages/backoffice-clients/clients-container/client-details/book-visit/book-visit.service';
import { getItemSession } from '@utilities';
import { ToastService } from '@modules';
import { PhoneNumberValidator } from '@directives';
import { BrokerAccountService } from '../../broker-account/broker-account.service';
import { AdminSettingService, AuthService } from '@services';

import { Subscription } from 'rxjs';

@Component({
  selector: 'ggi-journey-modal-new-client',
  templateUrl: './modal-new-client.component.html',
  styleUrls: ['./modal-new-client.component.scss'],
})
export class ModalNewClientComponent implements OnInit, OnDestroy {
  @Output() setClientId = new EventEmitter();

  public showCancel = false;
  public submitted = false;

  public roleRoute: string;

  public readonly MaskPhone = MaskPhone;

  public form: FormGroup;

  public broker: BrokerAccount;
  public questionI: QuestionI;

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: ModalNewClientService,
    private readonly bookVisitService: BookVisitService,
    private readonly toast: ToastService,
    private readonly brokerService: BrokerAccountService,
    private readonly settingService: AdminSettingService,
    public readonly auth: AuthService
  ) {}

  ngOnInit(): void {
    this.form = this.generateClientForm();

    this.roleRoute = getItemSession('user', 'route');
    if (
      this.auth.userRol() === 'BROKERLEADER' ||
      this.auth.userRol() === 'BROKER'
    ) {
      this.getBrokerInformation();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  public getBrokerInformation(): void {
    this.subscriptions.push(
      this.brokerService
        .getAccount()
        .subscribe(({ response }: ServerResponse) => {
          this.broker = response;
        })
    );

    this.subscriptions.push(
      this.settingService.getQuestion().subscribe((response) => {
        this.questionI = response.response;
      })
    );
  }

  /**
   * Generate client form
   */
  public generateClientForm(): FormGroup {
    return this.fb.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.pattern(RegexOnlyLetters),
          Validators.maxLength(50),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.pattern(RegexOnlyLetters),
          Validators.maxLength(50),
        ],
      ],
      email: ['', [Validators.required, Validators.pattern(RegexEmail)]],
      phone: [
        '',
        [
          Validators.required,
          Validators.pattern(RegexPhone),
          PhoneNumberValidator,
        ],
      ],
      company: ['', [Validators.maxLength(50)]],
      priority: ['baja', [Validators.required]],
      answer: ['', []],
      openAnswer: ['', []],
      notes: [''],
    });
  }

  /**
   * Show cancel modal
   */
  public cancel(): void {
    if (this.form.dirty) {
      this.showCancel = true;
    } else {
      this.close();
    }
  }

  /**
   * Close new client modal
   */
  public close(): void {
    this.service.close();
  }

  /**
   * Close cancel modal
   */
  public back(): void {
    this.showCancel = false;
  }

  /**
   * Select open answer in radios when focus input
   */
  public focusOpen(): void {
    this.form
      .get('answer')
      .setValue(this.questionI.answers.find((x) => x.isOpen).text);
  }

  /**
   * Submit add a new client
   */
  public submit(): void {
    this.submitted = true;
    if (this.form.invalid) {
      this.toast.error();
      this.form.markAllAsTouched();
      return;
    } else {
      const {
        firstName,
        lastName,
        email,
        phone,
        company,
        priority,
        notes,
        answer,
        openAnswer,
      } = this.form.value;
      const data: NewClient = {
        name: {
          firstName,
          lastName,
        },
        email,
        phone,
        company: company !== '' ? company : undefined,
        priority,
        notes,
      };

      let isOpenAnswer = false;
      const openAnswerItem = this.questionI.answers.find((x) => x.isOpen);
      if (openAnswerItem) {
        isOpenAnswer = openAnswerItem.text === answer;
      }

      if (isOpenAnswer && openAnswer) {
        data.answer = openAnswer;
        data.question = this.questionI.question;
      }
      if (answer && !isOpenAnswer) {
        data.answer = answer;
        data.question = this.questionI.question;
      }

      this.subscriptions.push(
        this.service
          .createClient(data)
          .subscribe(({ response }: ServerResponse) => {
            this.service.clientsList.next(true);
            this.bookVisitService.continue();
            this.setClientId.emit(response.id);
            this.close();
          })
      );
    }
  }
}
